import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  Wrap,
} from "@chakra-ui/react";
import { useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import {
  useApiError,
  withApiError,
} from "../../../components/ReactHookForm/ApiError";
import FormController from "../../../components/ReactHookForm/FormController";
import { CUSTOMERS_DOCUMENTS_STATES_VALUE_REFUSED } from "../../../constants/customersDocuments";
import { POLICIES_POLICY_VALUE_REFUSE } from "../../../constants/policies";
import useCanPerform from "../../../hooks/useCanPerform";
import useRequest from "../../../hooks/useRequest";
import generateApiUri from "../../../libraries/utils/generateApiUri";

function getFormId({ customerDocument }) {
  return `customers-documents-action-refuse-${customerDocument?.id}`;
}

const CustomersDocumentsActionRefuse = withApiError(
  /**
   * @typedef {object} Props
   * @property {import("../../../types/CustomerDocument").CustomerDocument} [customerDocument] associated customer document
   * @property {() => void} mutate
   * @property {(params: {isAllowed: boolean, trigger: () => void}) => import("react").ReactElement | null} render
   *
   * @param {Props} props
   */
  function CustomersDocumentsActionRefuse({
    render,
    customerDocument,
    mutate,
  }) {
    const intl = useIntl();

    const { isOpen, onOpen, onClose } = useDisclosure();

    const canperform = useCanPerform();

    const form = useForm({
      defaultValues: {
        data: {
          review: "",
        },
      },
    });
    const { handleSubmit, reset } = form;

    const buttonRef = useRef(null);

    const { request, isLoading, toastSuccess } = useRequest();

    const { resolveApiError, resetApiErrors } = useApiError();

    function onSubmit(fields) {
      request(
        generateApiUri({
          id: "@customers.documents.update",
          parameters: {
            documentId: customerDocument?.id,
          },
        }),
        {
          method: "post",
          body: {
            data: {
              ...fields.data,
              state: CUSTOMERS_DOCUMENTS_STATES_VALUE_REFUSED,
            },
          },
        },
      )
        .then(() => {
          toastSuccess({
            title: intl.formatMessage({ defaultMessage: "Document refusé" }),
          });
          onClose();
          reset();
          resetApiErrors();
          mutate();
        })
        .catch((response) => {
          resolveApiError({ response, form });
        });
    }

    return (
      <>
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          initialFocusRef={buttonRef}
          isCentered>
          <ModalOverlay />

          <ModalContent>
            <ModalHeader>
              {intl.formatMessage({ defaultMessage: "Refuser le document" })}
            </ModalHeader>

            <ModalCloseButton />

            <ModalBody>
              <FormProvider {...form}>
                <form
                  id={getFormId({ customerDocument })}
                  onSubmit={handleSubmit(onSubmit)}>
                  <FormController
                    type="textarea"
                    name="data.review"
                    label={intl.formatMessage({ defaultMessage: "Motif" })}
                    placeholder=""
                    rules={{ required: true }}
                  />
                </form>
              </FormProvider>
            </ModalBody>

            <ModalFooter>
              <Wrap>
                <Button onClick={onClose} variant="outline" colorScheme="gray">
                  {intl.formatMessage({ defaultMessage: "Annuler" })}
                </Button>

                <Button
                  ref={buttonRef}
                  type="submit"
                  form={getFormId({ customerDocument })}
                  isLoading={isLoading}
                  colorScheme="brandSecondary">
                  {intl.formatMessage({ defaultMessage: "Refuser" })}
                </Button>
              </Wrap>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {render({
          isAllowed: canperform({
            policies: POLICIES_POLICY_VALUE_REFUSE,
            resourcePolicies: customerDocument?.meta?.policies,
          }),
          trigger: onOpen,
        })}
      </>
    );
  },
);

export default CustomersDocumentsActionRefuse;
